// Community board ("Conversation") for Event and Group pages — design/026.
//
// Members: posts (leader-started unless "Let members start posts" is on),
// one level of replies, text posts (leaders can attach images / PDFs; older
// NVC Reflection posts still display), emoji reactions,
// ⋯ menu (edit 15 min / delete / report / block; leaders: pin / hide /
// restore), leader prompt + lock, per-board email setting.
// Non-members: a teaser with the post count + Register button.
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { Section, Spinner } from './ui';
import Modal from './Modal';

const QUICK_EMOJI = ['💛', '🙏', '🌱', '👏', '🤗'];
const REFLECTION_PARTS = [
  { key: 'observation', icon: '👀', label: 'Observation', hint: 'What happened, as a camera would see it' },
  { key: 'feelings',    icon: '💛', label: 'Feelings',    hint: 'What you felt' },
  { key: 'needs',       icon: '🌱', label: 'Needs',       hint: 'What needs were alive for you' },
  { key: 'request',     icon: '🙏', label: 'Request',     hint: 'A request of yourself or others (optional)' },
];
const REPORT_REASONS = [
  { value: 'HARASSMENT',    label: 'Harassment or bullying' },
  { value: 'INAPPROPRIATE', label: 'Inappropriate content' },
  { value: 'SPAM',          label: 'Spam' },
  { value: 'OTHER',         label: 'Something else' },
];

// Server messages may start with a code ("CONTENT_NOT_ALLOWED: …"); show only the sentence.
const errText = (e) => (e?.data?.message || e?.message || 'Something went wrong').replace(/^[A-Z_]+:\s*/, '');

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * @param kind        'event' | 'group'
 * @param id          entity id
 * @param onRegister  () => void — teaser's Register button (non-members)
 */
export default function Conversation({ kind, id, onRegister }) {
  const [board, setBoard] = useState(null);
  const [error, setError] = useState(null);
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const b = await api.getBoard(kind, id);
      setBoard(b);
      setError(null);
      if (b?.member && b.unreadCount > 0) api.markBoardRead(kind, id).catch(() => {});
    } catch (e) { setError(errText(e)); }
  }, [kind, id]);

  useEffect(() => { load(); }, [load]);

  // Every write goes through here: reload + error alert. `gate` = this write
  // is a post/reply/edit, which needs the community guidelines accepted first.
  const run = useCallback(async (fn, gate = false) => {
    // Not accepted yet → show the guidelines; the draft stays in the
    // composer so the person presses Post again after agreeing.
    if (gate && board && board.member && !board.guidelinesAccepted) {
      setGuidelinesOpen(true);
      return false;
    }
    try { await fn(); await load(); return true; }
    catch (e) {
      if (e?.data?.code === 'GUIDELINES_REQUIRED') {
        setGuidelinesOpen(true);
        return false;
      }
      alert(errText(e));
      return false;
    }
  }, [board, load]);

  const acceptGuidelines = async () => {
    try {
      await api.acceptCommunityGuidelines();
      setBoard((b) => (b ? { ...b, guidelinesAccepted: true } : b));
      setGuidelinesOpen(false);
    } catch (e) { alert(errText(e)); }
  };

  if (error) return <Section title="💬 Conversation"><div style={{ color: 'var(--muted)' }}>{error}</div></Section>;
  if (!board) return <Section title="💬 Conversation"><Spinner /></Section>;

  // ---------------------------------------------------------------- teaser
  if (!board.member) {
    const n = board.postCount || 0;
    return (
      <Section title="💬 Conversation">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200, color: 'var(--muted)', fontSize: '0.9rem' }}>
            {n > 0
              ? <><b style={{ color: 'var(--ink)' }}>{n} {n === 1 ? 'post' : 'posts'}</b> — register to join the conversation.</>
              : 'Register to join the conversation.'}
          </div>
          {onRegister && <button className="btn btn-primary" onClick={onRegister}>Register →</button>}
        </div>
      </Section>
    );
  }

  // ---------------------------------------------------------------- member view
  const posts = board.posts || [];
  return (
    <Section
      title="💬 Conversation"
      action={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {board.locked && <span className="pill" style={{ background: '#f3f4f6', color: 'var(--muted)' }}>🔒 Read-only</span>}
          <NotifySelect value={board.notify} onChange={(v) => run(() => api.setBoardNotify(kind, id, v))} />
        </div>
      }
    >
      {board.canModerate && <LeaderSettings kind={kind} id={id} board={board} run={run} />}

      {board.prompt && board.membersCanPost && (
        <div style={{ background: 'var(--card-soft, #f9fafb)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.6, color: 'var(--muted)', textTransform: 'uppercase' }}>Conversation prompt</div>
          <div style={{ marginTop: 2 }}>{board.prompt}</div>
        </div>
      )}

      {board.canStartThread ? (
        <Composer
          key="new-post"
          title="Post a conversation"
          replyNote={board.locked
            ? '🔒 The conversation is closed — members can read this post but not reply.'
            : '💬 Members will be able to reply to this post.'}
          allowRecap={board.canModerate && kind === 'event'}
          onUpload={board.canModerate ? (file) => api.uploadBoardAttachment(kind, id, file) : null}
          placeholder={board.canModerate ? 'Start a conversation…' : 'Share something with the group…'}
          submitLabel="Post"
          onSubmit={(payload, asRecap) => run(() => (asRecap
            ? api.postBoardRecap(kind, id, payload.body)
            : api.createBoardPost(kind, id, payload)), true)}
        />
      ) : (
        !board.locked && posts.length === 0 && (
          <div style={{ color: 'var(--muted)', fontSize: '0.875rem', marginBottom: 8 }}>
            The leader hasn't started a conversation yet. You'll be able to reply once they do.
          </div>
        )
      )}

      {posts.length === 0 ? (
        board.canStartThread && <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>No posts yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
          {posts.map((p) => (
            <Thread key={p.id} post={p} board={board} kind={kind} id={id} run={run} />
          ))}
        </div>
      )}

      {guidelinesOpen && (
        <GuidelinesModal
          onAccept={acceptGuidelines}
          onClose={() => setGuidelinesOpen(false)}
        />
      )}
    </Section>
  );
}

// -------------------------------------------------------------------- pieces

function NotifySelect({ value, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 500 }}>
      Email me:
      <select
        className="input"
        value={value || 'DIGEST'}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 'auto', padding: '4px 8px', fontSize: '0.8rem' }}
      >
        <option value="DIGEST">A daily summary of new posts</option>
        <option value="ALL">Every new post and reply</option>
        <option value="NONE">Nothing</option>
      </select>
    </label>
  );
}

function LeaderSettings({ kind, id, board, run }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(board.prompt || '');
  useEffect(() => { setPrompt(board.prompt || ''); }, [board.prompt]);
  return (
    <div style={{ marginBottom: 12 }}>
      <button className="btn btn-sm" onClick={() => setOpen((o) => !o)}>
        {open ? '▾' : '▸'} Conversation settings (only you see this)
      </button>
      {open && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginTop: 8 }}>
          <div>
            <button className="btn btn-sm"
              onClick={() => run(() => api.updateBoardSettings(kind, id, { locked: !board.locked }))}>
              {board.locked ? '🔓 Reopen conversation' : '🔒 Close conversation (read-only)'}
            </button>
          </div>
          <div className="form-field" style={{ marginTop: 12, marginBottom: 0 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!board.membersCanPost}
                onChange={(e) => run(() => api.updateBoardSettings(kind, id, { membersCanPost: e.target.checked }))} />
              <span>Let members start posts</span>
            </label>
            <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>
              Members can always reply to posts. Turn this on to let them start their own posts too.
            </div>
          </div>
          {/* The prompt guides what members post, so it only
              applies when members can start posts. */}
          {board.membersCanPost && (
            <div className="form-field" style={{ marginTop: 12, marginLeft: 26, marginBottom: 0 }}>
              <label>Conversation prompt</label>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: 6 }}>
                Optional. A question shown to members at the top of the conversation to guide what they post. Leave empty for none.
              </div>
              <textarea className="textarea" value={prompt} onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g. Share one need that came alive for you tonight." />
              <div style={{ marginTop: 8 }}>
                <button className="btn btn-sm btn-primary"
                  disabled={(prompt || '') === (board.prompt || '')}
                  onClick={() => run(() => api.updateBoardSettings(kind, id, { prompt }))}>Save prompt</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Post composer — new posts, replies and edits. Text only (Reflection mode
 * removed 2026-10-02); older Reflection posts can still be edited in place.
 * `onUpload` (leaders only) enables attaching up to 4 images / PDFs.
 */
const MAX_FILES = 4;
const MAX_MB = 10;
function Composer({ initial, placeholder, submitLabel, onSubmit, onCancel, onUpload, allowRecap = false, compact = false, title, replyNote }) {
  const reflection = initial?.kind === 'REFLECTION';
  const [body, setBody] = useState(initial?.body || '');
  const [parts, setParts] = useState({
    observation: initial?.observation || '', feelings: initial?.feelings || '',
    needs: initial?.needs || '', request: initial?.request || '',
  });
  const [formOpen, setFormOpen] = useState(false);
  const [formUrl, setFormUrl] = useState('');
  const [files, setFiles] = useState([]);   // uploaded { id, filename, image, url } or { uploading, key }
  const [asRecap, setAsRecap] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);
  const countRef = useRef(0);

  const uploading = files.some((f) => f.uploading);
  const canAttach = !!onUpload && !initial && !asRecap;
  const formOk = !formUrl.trim() || /^https:\/\/\S+$/i.test(formUrl.trim());
  const empty = reflection
    ? !Object.values(parts).some((v) => v.trim())
    : !body.trim() && !(canAttach && (files.some((f) => f.id) || formUrl.trim()));

  const pick = async (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = '';
    countRef.current = files.length;
    for (const file of chosen) {
      if (countRef.current >= MAX_FILES) { alert(`Up to ${MAX_FILES} files per post.`); break; }
      if (file.size > MAX_MB * 1024 * 1024) { alert(`"${file.name}" is over ${MAX_MB} MB.`); continue; }
      countRef.current += 1;
      const tmp = { uploading: file.name, key: `${file.name}-${Date.now()}-${countRef.current}` };
      setFiles((fs) => [...fs, tmp]);
      try {
        const a = await onUpload(file);
        setFiles((fs) => fs.map((f) => (f === tmp ? a : f)));
      } catch (err) {
        countRef.current -= 1;
        setFiles((fs) => fs.filter((f) => f !== tmp));
        alert(errText(err));
      }
    }
  };

  const submit = async () => {
    if (empty || uploading) return;
    if (!formOk) { alert("Paste the form's full link, starting with https://"); return; }
    setBusy(true);
    const ids = canAttach ? files.filter((f) => f.id).map((f) => f.id) : [];
    const payload = reflection
      ? { kind: 'REFLECTION', ...parts }
      : {
        kind: 'POST', body,
        ...(ids.length ? { attachmentIds: ids } : {}),
        ...(canAttach && formUrl.trim() ? { formUrl: formUrl.trim() } : {}),
      };
    const ok = await onSubmit(payload, asRecap);
    setBusy(false);
    if (ok !== false && !initial) {
      setBody(''); setFiles([]); setFormUrl(''); setFormOpen(false); setAsRecap(false);
    }
  };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: compact ? 8 : 12, background: '#fff', marginBottom: compact ? 0 : 8 }}>
      {(title || allowRecap) && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {title && <div style={{ fontWeight: 700 }}>{title}</div>}
          {title && replyNote && <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{replyNote}</span>}
          {allowRecap && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', marginLeft: 'auto', cursor: 'pointer' }}>
              <input type="checkbox" checked={asRecap} onChange={(e) => setAsRecap(e.target.checked)} />
              📌 Post as recap (pinned)
            </label>
          )}
        </div>
      )}
      {reflection ? (
        <div style={{ display: 'grid', gap: 8 }}>
          {REFLECTION_PARTS.map((r) => (
            <div key={r.key}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: 2 }}>{r.icon} {r.label}</div>
              <textarea className="textarea" rows={2} value={parts[r.key]} placeholder={r.hint}
                onChange={(e) => setParts((p) => ({ ...p, [r.key]: e.target.value }))} />
            </div>
          ))}
        </div>
      ) : (
        <textarea className="textarea" rows={compact ? 2 : 3} value={body}
          placeholder={asRecap ? 'Key takeaways, links, the recording, next steps…' : placeholder}
          onChange={(e) => setBody(e.target.value)} />
      )}
      {canAttach && formOpen && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input className="input" style={{ flex: 1 }} value={formUrl} autoFocus
              placeholder="https://tally.so/r/… (or Typeform, Google Forms)"
              onChange={(e) => setFormUrl(e.target.value)} />
            <button type="button" className="btn btn-sm" title="Remove form"
              onClick={() => { setFormUrl(''); setFormOpen(false); }}>✕</button>
          </div>
          <div style={{ fontSize: '0.75rem', color: formOk ? 'var(--muted)' : '#b91c1c', marginTop: 4 }}>
            {formOk ? 'Members fill it in right inside the post. Paste the form’s share link.' : 'The link must start with https://'}
          </div>
        </div>
      )}
      {canAttach && files.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          {files.map((f) => (
            <div key={f.id || f.key} style={{
              display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--border)', borderRadius: 8,
              padding: 4, paddingRight: 8, fontSize: '0.8rem', background: 'var(--card-soft, #f9fafb)', maxWidth: 220,
            }}>
              {f.uploading ? <span style={{ padding: '0 4px' }}>⏳</span>
                : f.image ? <img src={api.iconUrl(f.url)} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6 }} />
                  : <span style={{ padding: '0 4px' }}>📄</span>}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.uploading || f.filename}</span>
              {!f.uploading && (
                <button type="button" className="btn btn-sm" title="Remove" aria-label="Remove attachment"
                  style={{ padding: '0 6px', border: 'none', background: 'none' }}
                  onClick={() => setFiles((fs) => fs.filter((x) => x !== f))}>✕</button>
              )}
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 8 }}>
        {canAttach && (
          <>
            <input ref={fileInput} type="file" multiple hidden
              accept="image/jpeg,image/png,image/gif,image/webp,image/heic,application/pdf,.heic,.pdf"
              onChange={pick} />
            <button type="button" className="btn btn-sm" disabled={busy || files.length >= MAX_FILES}
              onClick={() => fileInput.current?.click()} title={`Images or PDFs — up to ${MAX_FILES}, ${MAX_MB} MB each`}>
              📎 Image or PDF
            </button>
            {!formOpen && (
              <button type="button" className="btn btn-sm" disabled={busy} onClick={() => setFormOpen(true)}
                title="Embed a survey (Tally, Typeform, Google Forms)">
                📋 Survey form
              </button>
            )}
          </>
        )}
        <span style={{ flex: 1, fontSize: '0.8rem', color: 'var(--muted)' }}>{!title && replyNote ? replyNote : ''}</span>
        {onCancel && <button className="btn btn-sm" onClick={onCancel} disabled={busy}>Cancel</button>}
        <button className="btn btn-sm btn-primary" onClick={submit} disabled={busy || empty || uploading}>
          {busy ? 'Posting…' : uploading ? 'Uploading…' : (asRecap ? 'Post recap' : submitLabel)}
        </button>
      </div>
    </div>
  );
}

/**
 * Survey form embedded in a post. Tally share links (/r/ID) become the
 * embed URL; Typeform IDs / links are normalized; other https links pass
 * through (Google Forms, Jotform…).
 */
export function formEmbedUrl(raw) {
  const s = (raw || '').trim();
  if (!s) return null;
  const tally = s.match(/tally\.so\/(?:r|embed)\/([A-Za-z0-9]+)/i);
  if (tally) return `https://tally.so/embed/${tally[1]}?alignLeft=1&hideTitle=0&transparentBackground=1&dynamicHeight=1`;
  const tf = s.match(/typeform\.com\/to\/([A-Za-z0-9]+)/i);
  if (tf) return `https://form.typeform.com/to/${tf[1]}`;
  return /^https:\/\//i.test(s) ? s : null;
}

function PostForm({ url }) {
  const src = formEmbedUrl(url);
  if (!src) return null;
  return (
    <div style={{ marginTop: 8, maxWidth: 640 }}>
      <iframe src={src} title="Survey" loading="lazy"
        style={{ width: '100%', height: 520, border: '1px solid var(--border)', borderRadius: 8, background: '#fff' }}
        allow="camera; microphone; autoplay; encrypted-media; fullscreen" />
    </div>
  );
}

/** Images (thumbnails; click for full size) and PDFs (chips) on a post. */
function Attachments({ items }) {
  if (!items || items.length === 0) return null;
  const images = items.filter((a) => a.image);
  const docs = items.filter((a) => !a.image);
  return (
    <div style={{ marginTop: 8 }}>
      {images.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(images.length, 2)}, minmax(0, 1fr))`, gap: 6, maxWidth: 520 }}>
          {images.map((a) => (
            <a key={a.id} href={api.iconUrl(a.url)} target="_blank" rel="noreferrer" title={a.filename}>
              <img src={api.iconUrl(a.url)} alt={a.filename}
                style={{
                  width: '100%', maxHeight: images.length === 1 ? 420 : 220,
                  objectFit: images.length === 1 ? 'contain' : 'cover',
                  borderRadius: 8, border: '1px solid var(--border)', background: '#f3f4f6', display: 'block',
                }} />
            </a>
          ))}
        </div>
      )}
      {docs.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: images.length ? 8 : 0 }}>
          {docs.map((a) => (
            <a key={a.id} href={api.iconUrl(a.url)} target="_blank" rel="noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid var(--border)', borderRadius: 8,
                padding: '6px 10px', fontSize: '0.85rem', background: 'var(--card-soft, #f9fafb)', textDecoration: 'none', color: 'inherit',
              }}>
              📄 <span>{a.filename}</span>
              <span style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>{(a.sizeBytes / (1024 * 1024)).toFixed(1)} MB</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function Thread({ post, board, kind, id, run }) {
  const [replying, setReplying] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const replies = post.replies || [];
  const shown = showAll ? replies : replies.slice(-2);
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: post.pinned ? '#fffbeb' : '#fff' }}>
      <PostItem post={post} board={board} kind={kind} id={id} run={run} topLevel
        onReply={board.canReply ? () => setReplying((r) => !r) : null} />
      {(replies.length > 0 || replying) && (
        <div style={{ marginTop: 10, marginLeft: 18, paddingLeft: 12, borderLeft: '2px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {replies.length > 2 && !showAll && (
            <a onClick={() => setShowAll(true)} style={{ cursor: 'pointer', fontSize: '0.8rem' }}>
              Show all {replies.length} replies
            </a>
          )}
          {shown.map((r) => (
            <PostItem key={r.id} post={r} board={board} kind={kind} id={id} run={run} />
          ))}
          {replying && (
            <Composer compact placeholder="Write a reply…" submitLabel="Reply"
              onUpload={board.canModerate ? (file) => api.uploadBoardAttachment(kind, id, file) : null}
              onCancel={() => setReplying(false)}
              onSubmit={async (payload) => {
                const ok = await run(() => api.createBoardPost(kind, id, { ...payload, parentId: post.id }), true);
                if (ok) { setReplying(false); setShowAll(true); }
                return ok;
              }} />
          )}
        </div>
      )}
    </div>
  );
}

function PostItem({ post, board, kind, id, run, topLevel = false, onReply }) {
  const [editing, setEditing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [picker, setPicker] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const name = post.author?.name || 'Member';
  const react = (emoji) => { setPicker(false); run(() => api.reactBoardPost(kind, id, post.id, emoji)); };

  const menuItems = [];
  if (post.canEdit) menuItems.push(['Edit', () => setEditing(true)]);
  if (post.mine) {
    menuItems.push(['Delete', () => { if (window.confirm('Delete this post?')) run(() => api.deleteBoardPost(kind, id, post.id)); }]);
  } else {
    if (board.canModerate && !post.hidden) {
      menuItems.push(['Hide (remove for everyone)', () => {
        if (window.confirm('Hide this post from everyone?')) run(() => api.deleteBoardPost(kind, id, post.id));
      }]);
    }
    menuItems.push(['Report', () => setReporting(true)]);
    menuItems.push([`Block ${name}`, () => {
      if (window.confirm(`Block ${name}? You won't see their posts or replies anywhere.`)) {
        run(() => api.blockPerson(post.author.id));
      }
    }]);
  }
  if (board.canModerate && topLevel && !post.hidden) {
    menuItems.push([post.pinned ? 'Unpin' : 'Pin to top', () => run(() => api.pinBoardPost(kind, id, post.id, !post.pinned))]);
  }
  if (board.canModerate && post.hidden) {
    menuItems.push(['Restore', () => run(() => api.restoreBoardPost(kind, id, post.id))]);
  }

  return (
    <div style={{ opacity: post.hidden ? 0.6 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%', background: 'var(--indigo, #4f46e5)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem', flexShrink: 0,
        }}>{name.charAt(0).toUpperCase()}</div>
        <div style={{ flex: 1, minWidth: 0, fontSize: '0.85rem' }}>
          <b>{name}</b>
          <span style={{ color: 'var(--muted)' }}> · {timeAgo(post.createdAt)}{post.edited ? ' · edited' : ''}</span>
          {post.kind === 'RECAP' && <span className="pill" style={{ marginLeft: 8, background: '#fef3c7', color: '#92400e' }}>📌 Recap</span>}
          {post.pinned && post.kind !== 'RECAP' && <span className="pill" style={{ marginLeft: 8, background: '#fef3c7', color: '#92400e' }}>📌 Pinned</span>}
        </div>
        {menuItems.length > 0 && (
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button className="btn btn-sm" onClick={() => setMenuOpen((o) => !o)}
              aria-label={post.mine ? 'Edit or delete' : 'Report, block and more'}
              title={post.mine ? 'Edit or delete' : 'Report, block and more'}>⋯</button>
            {menuOpen && (
              <div style={{
                position: 'absolute', right: 0, top: '100%', zIndex: 20, background: '#fff', minWidth: 190,
                border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-card)', padding: 4,
              }}>
                {menuItems.map(([label, fn]) => (
                  <button key={label} className="btn btn-sm"
                    style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'none' }}
                    onClick={() => { setMenuOpen(false); fn(); }}>{label}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {post.hidden && (
        <div style={{ fontSize: '0.75rem', color: '#991b1b', marginTop: 4 }}>
          Hidden from members ({post.hiddenReason === 'AUTO_REPORTED' ? 'reported by several people' : 'removed by leader'}).
        </div>
      )}

      <div style={{ marginTop: 6, marginLeft: 36 }}>
        {editing ? (
          <Composer initial={post} submitLabel="Save" onCancel={() => setEditing(false)}
            onSubmit={async (payload) => {
              const ok = await run(() => api.editBoardPost(kind, id, post.id, payload), true);
              if (ok) setEditing(false);
              return ok;
            }} />
        ) : post.kind === 'REFLECTION' ? (
          <ReflectionCard post={post} />
        ) : (
          post.body ? <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{post.body}</div> : null
        )}
        {!editing && <Attachments items={post.attachments} />}
        {!editing && post.formUrl && <PostForm url={post.formUrl} />}

        {!editing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {(post.reactions || []).map((r) => (
              <button key={r.emoji} className="btn btn-sm" onClick={() => react(r.emoji)}
                style={{
                  padding: '2px 8px', borderRadius: 999,
                  background: r.mine ? '#eef2ff' : undefined, borderColor: r.mine ? '#a5b4fc' : undefined,
                }}>
                {r.emoji} {r.count}
              </button>
            ))}
            {!post.hidden && !(post.reactions || []).some((r) => r.emoji === '💛') && (
              <button className="btn btn-sm" onClick={() => react('💛')} title="Resonates"
                style={{ padding: '2px 8px', borderRadius: 999 }}>💛</button>
            )}
            {!post.hidden && (
              <span style={{ position: 'relative' }}>
                <button className="btn btn-sm" onClick={() => setPicker((p) => !p)} title="Add reaction"
                  style={{ padding: '2px 8px', borderRadius: 999 }}>＋</button>
                {picker && (
                  <span style={{
                    position: 'absolute', left: 0, top: '110%', zIndex: 20, display: 'flex', gap: 4, padding: 6,
                    background: '#fff', border: '1px solid var(--border)', borderRadius: 999, boxShadow: 'var(--shadow-card)',
                  }}>
                    {QUICK_EMOJI.map((e) => (
                      <button key={e} className="btn btn-sm" style={{ border: 'none', background: 'none', fontSize: '1.1rem', padding: '2px 4px' }}
                        onClick={() => react(e)}>{e}</button>
                    ))}
                  </span>
                )}
              </span>
            )}
            {onReply && !post.hidden && (
              <button className="btn btn-sm" onClick={onReply} style={{ marginLeft: 4 }}>↩ Reply</button>
            )}
            {/* design/029 — report is always one click away, not hidden in a menu. */}
            {!post.mine && !post.hidden && (
              <button className="btn btn-sm" onClick={() => setReporting(true)}
                title={`Report this ${topLevel ? 'post' : 'reply'} or block ${name}`}
                style={{ marginLeft: 4, color: 'var(--muted)' }}>⚑ Report</button>
            )}
          </div>
        )}
      </div>

      {reporting && (
        <ReportModal
          name={name}
          canBlock={!!post.author?.id}
          onClose={() => setReporting(false)}
          onSubmit={(reason, note) => run(() => api.reportBoardPost(kind, id, post.id, reason, note))}
          onBlock={async () => {
            const ok = await run(() => api.blockPerson(post.author.id));
            if (ok) setReporting(false);
          }} />
      )}
    </div>
  );
}

function ReflectionCard({ post }) {
  const parts = REFLECTION_PARTS.filter((r) => post[r.key]);
  return (
    <div style={{ display: 'grid', gap: 6, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 10 }}>
      {parts.map((r) => (
        <div key={r.key}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.5, color: '#166534', textTransform: 'uppercase' }}>
            {r.icon} {r.label}
          </div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{post[r.key]}</div>
        </div>
      ))}
    </div>
  );
}

function ReportModal({ name, canBlock, onClose, onSubmit, onBlock }) {
  const [reason, setReason] = useState('HARASSMENT');
  const [note, setNote] = useState('');
  const [step, setStep] = useState('form');   // form → done (design/029)
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    const ok = await onSubmit(reason, note);
    setBusy(false);
    if (ok) setStep('done');
  };
  const block = async () => {
    setBusy(true);
    await onBlock();
    setBusy(false);
  };

  if (step === 'done') {
    return (
      <Modal title="Thanks for reporting" onClose={onClose}
        footer={<>
          {canBlock && (
            <button className="btn" disabled={busy} onClick={block}
              style={{ color: '#b91c1c', borderColor: '#fca5a5' }}>{busy ? 'Blocking…' : `Block ${name}`}</button>
          )}
          <button className="btn btn-primary" disabled={busy} onClick={onClose}>Done</button>
        </>}>
        <p style={{ marginTop: 0 }}>
          The leader and our moderation team have been notified. We review every report within 24 hours.
        </p>
        {canBlock && (
          <p style={{ marginBottom: 0, color: 'var(--muted)' }}>
            You can also block {name}. You won't see their posts or replies anywhere, and they won't be told.
          </p>
        )}
      </Modal>
    );
  }

  return (
    <Modal title="Report this post" onClose={onClose}
      footer={<>
        <button className="btn" disabled={busy} onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" disabled={busy} onClick={submit}>{busy ? 'Reporting…' : 'Report'}</button>
      </>}>
      <div className="form-field">
        <label>What's wrong?</label>
        <select className="input" value={reason} onChange={(e) => setReason(e.target.value)}>
          {REPORT_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </div>
      <div className="form-field">
        <label>Anything else we should know? (optional)</label>
        <textarea className="textarea" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
        Reports are private. The leader and our moderation team review every report within 24 hours; if several
        people report a post it's hidden right away until it's reviewed.
      </div>
    </Modal>
  );
}

export function GuidelinesModal({ onAccept, onClose }) {
  return (
    <Modal title="Community guidelines" onClose={onClose}
      footer={<>
        <button className="btn" onClick={onClose}>Not now</button>
        <button className="btn btn-primary" onClick={onAccept}>I agree — continue</button>
      </>}>
      <p style={{ marginTop: 0 }}>
        <b>Compassion Practice has zero tolerance for objectionable content or abusive behavior.</b> Posts that break
        these guidelines are removed, and the people who post them are removed from the community.
      </p>
      <p>These conversations are a space for compassionate practice. Before your first post, please agree to:</p>
      <ul style={{ lineHeight: 1.7, paddingLeft: 20 }}>
        <li>Speak from your own experience — observations, feelings, needs and requests.</li>
        <li>Listen with empathy; no judging, labelling, or diagnosing others.</li>
        <li>No harassment, hate, threats, sexual content, or spam.</li>
        <li>Keep what others share here private — don't copy it elsewhere.</li>
        <li>Don't post personal contact details (yours or anyone else's).</li>
      </ul>
      <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: 0 }}>
        You can report posts or block people from the ⋯ menu. Our team reviews every report within 24 hours.
        Questions? <a href="mailto:support@mediumnumber.com">support@mediumnumber.com</a>
      </p>
    </Modal>
  );
}

// -------------------------------------------------------------------- draft setup

/** Empty value for {@link ConversationSetup}. */
export const EMPTY_CONVERSATION_SETUP = {
  mode: 'POST', body: '',
  parts: { observation: '', feelings: '', needs: '', request: '' },
  membersCanPost: false, prompt: '', locked: false,
};

/** True when the setup has an opening post to publish on Create. */
export function setupHasPost(v) {
  return v.mode === 'REFLECTION'
    ? Object.values(v.parts).some((x) => x.trim())
    : !!v.body.trim();
}

/**
 * Conversation while an Event / Group is still a draft. Same layout as after
 * creation — the leader's "Conversation settings" panel and the "Post a
 * conversation" box — but with no Post / Save prompt buttons: the parent
 * saves everything when the leader presses Create
 * (see {@link applyConversationSetup}). Nobody is a member yet, so nothing
 * is lost by waiting and no one is notified.
 */
export function ConversationSetup({ value, onChange }) {
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <Section title="💬 Conversation">
      <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: 10 }}>
        Set up the conversation now — everything here is saved when you press <b>Create</b>.
      </div>

      {/* Same panel as LeaderSettings, but values are held until Create. */}
      <div style={{ marginBottom: 12 }}>
        <button className="btn btn-sm" onClick={() => setSettingsOpen((o) => !o)}>
          {settingsOpen ? '▾' : '▸'} Conversation settings (only you see this)
        </button>
        {settingsOpen && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginTop: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!value.locked} onChange={(e) => set({ locked: e.target.checked })} />
              <span>🔒 Start closed (read-only)</span>
            </label>
            <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4, marginLeft: 26 }}>
              Members can read but not post or reply until you reopen it.
            </div>
            <div className="form-field" style={{ marginTop: 12, marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!value.membersCanPost}
                  onChange={(e) => set({ membersCanPost: e.target.checked })} />
                <span>Let members start posts</span>
              </label>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>
                Members can always reply to posts. Turn this on to let them start their own posts too.
              </div>
            </div>
            {value.membersCanPost && (
              <div className="form-field" style={{ marginTop: 12, marginLeft: 26, marginBottom: 0 }}>
                <label>Conversation prompt</label>
                <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: 6 }}>
                  Optional. A question shown to members at the top of the conversation to guide what they post. Leave empty for none.
                </div>
                <textarea className="textarea" value={value.prompt} onChange={(e) => set({ prompt: e.target.value })}
                  placeholder="e.g. Share one need that came alive for you tonight." />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Same look as the "Post a conversation" composer, minus the Post button. */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, background: '#fff' }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Post a conversation <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: '0.85rem' }}>(optional)</span></div>
        {value.mode === 'REFLECTION' ? (
          <div style={{ display: 'grid', gap: 8 }}>
            {REFLECTION_PARTS.map((r) => (
              <div key={r.key}>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: 2 }}>{r.icon} {r.label}</div>
                <textarea className="textarea" rows={2} value={value.parts[r.key]} placeholder={r.hint}
                  onChange={(e) => set({ parts: { ...value.parts, [r.key]: e.target.value } })} />
              </div>
            ))}
          </div>
        ) : (
          <textarea className="textarea" rows={3} value={value.body}
            placeholder="Start a conversation…"
            onChange={(e) => set({ body: e.target.value })} />
        )}
        <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: 8 }}>
          {value.locked
            ? '🔒 The conversation will start closed — members can read this post but not reply.'
            : '💬 Members will be able to reply to this post.'}
          {' '}Posted when you press <b>Create</b>. By posting you agree to the{' '}
          <a onClick={() => setShowGuidelines(true)} style={{ cursor: 'pointer' }}>community guidelines</a>.
        </div>
      </div>

      {showGuidelines && (
        <GuidelinesModal onAccept={() => setShowGuidelines(false)} onClose={() => setShowGuidelines(false)} />
      )}
    </Section>
  );
}

/**
 * Called by the Event / Group page right after Create succeeds: saves the
 * board settings and the opening post (accepting the guidelines, which the
 * set-up form told the leader about). Throws on failure.
 */
export async function applyConversationSetup(kind, id, v) {
  const hasPost = setupHasPost(v);
  // Post first so a "start closed" board still gets its opening post
  // (leaders can post on locked boards anyway, but this keeps it simple).
  if (hasPost) {
    await api.acceptCommunityGuidelines();
    const payload = v.mode === 'REFLECTION'
      ? { kind: 'REFLECTION', ...v.parts }
      : { kind: 'POST', body: v.body };
    await api.createBoardPost(kind, id, payload);
  }
  if (v.membersCanPost || v.locked) {
    await api.updateBoardSettings(kind, id, {
      membersCanPost: !!v.membersCanPost,
      prompt: v.membersCanPost ? v.prompt.trim() : undefined,
      locked: !!v.locked,
    });
  }
}
